import { findParaguayCity, normalizeCatalogText } from "./geography.js";
export * from "./geography.js";
export * from "./categories.js";
export * from "./site-seo.js";
export const DISCOVERY_STATUSES = [
    "DISCOVERED",
    "NEEDS_REVIEW",
    "VALID",
    "INVALID",
    "DUPLICATE"
];
export const PUBLICATION_STATUSES = [
    "NOT_PUBLISHED",
    "PUBLISHED_UNVERIFIED",
    "PUBLISHED_VERIFIED",
    "SUSPENDED",
    "OPTED_OUT",
    "CLOSED"
];
export const COMMERCIAL_STATUSES = [
    "NOT_CONTACTED",
    "CONTACT_PENDING",
    "CONTACTED",
    "MEETING",
    "INTERESTED",
    "PROPOSAL_SENT",
    "ONBOARDING",
    "ACTIVE_CLIENT",
    "DECLINED",
    "DO_NOT_CONTACT"
];
export const CATALOG_STATUSES = [
    "NO_CATALOG",
    "BASIC_CATALOG",
    "COMPLETE_CATALOG",
    "CATALOG_STALE"
];
export const INVENTORY_STATUSES = [
    "NOT_CONNECTED",
    "SYNCING",
    "HEALTHY",
    "STALE",
    "ERROR",
    "DISABLED"
];
export const QUALITY_STATUSES = [
    "COMPLETE",
    "MISSING_LOCATION",
    "MISSING_CONTACT",
    "CONFLICTING_DATA",
    "REVIEW_REQUIRED"
];
export const SOURCE_REVIEW_STATUSES = [
    "PENDING_REVIEW",
    "APPROVED",
    "LIMITED",
    "SUSPENDED",
    "PROHIBITED"
];
export const AVAILABILITY_STATUSES = [
    "IN_STOCK",
    "LOW_STOCK",
    "OUT_OF_STOCK",
    "PREORDER",
    "ON_REQUEST",
    "UNKNOWN"
];
export const PUBLIC_CHANNEL_TYPES = [
    "PHYSICAL_LOCATION",
    "ONLINE_STORE",
    "ONLINE_AND_PHYSICAL",
    "UNKNOWN"
];
export const WEBSITE_REACHABILITY_STATUSES = ["REACHABLE", "UNREACHABLE", "NOT_CHECKED"];
export const MERCHANT_INVITE_STATUSES = ["ACTIVE", "CLAIMED", "REVOKED", "EXPIRED"];
export const MERCHANT_PRODUCT_PUBLICATION_STATUSES = ["DRAFT", "PUBLISHED"];
export const ACCESS_REQUEST_CONSENT_VERSION = "puntopy-access-v1";
class AccessRequestValidationError extends Error {
}
function accessRequestRecord(value) {
    return typeof value === "object" && value !== null;
}
function accessRequestText(value, field, minimum, maximum) {
    if (typeof value !== "string")
        throw new AccessRequestValidationError(`El campo ${field} es obligatorio.`);
    const normalized = value.trim().replace(/\s+/g, " ");
    if (normalized.length < minimum || normalized.length > maximum) {
        throw new AccessRequestValidationError(`${field} debe tener entre ${minimum} y ${maximum} caracteres.`);
    }
    return normalized;
}
function accessRequestEmail(value) {
    const email = accessRequestText(value, "email", 5, 254).toLocaleLowerCase("en-US");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        throw new AccessRequestValidationError("Ingresá un correo electrónico válido.");
    }
    return email;
}
function accessRequestRuc(value) {
    const rawRuc = accessRequestText(value, "RUC", 6, 20).replace(/[.\s]/g, "");
    if (!/^\d{5,11}-?\d$/.test(rawRuc)) {
        throw new AccessRequestValidationError("Ingresá el RUC con sus dígitos y, si corresponde, el guion verificador.");
    }
    const digits = rawRuc.replace(/\D/g, "");
    if (digits.length < 6 || digits.length > 12) {
        throw new AccessRequestValidationError("El RUC debe contener entre 6 y 12 dígitos.");
    }
    return digits;
}
function accessRequestWebsite(value) {
    if (value === null || value === undefined || value === "")
        return null;
    if (typeof value !== "string")
        throw new AccessRequestValidationError("El sitio web no es válido.");
    const trimmed = value.trim();
    if (trimmed.length === 0)
        return null;
    if (trimmed.length > 240)
        throw new AccessRequestValidationError("El sitio web no puede superar 240 caracteres.");
    const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    try {
        const website = new URL(candidate);
        if ((website.protocol !== "https:" && website.protocol !== "http:") || website.hostname.length < 3) {
            throw new Error("unsupported URL");
        }
        website.hash = "";
        return website.toString();
    }
    catch {
        throw new AccessRequestValidationError("Ingresá un sitio web válido, por ejemplo https://empresa.com.py.");
    }
}
export function validateAccessRequest(value) {
    try {
        if (!accessRequestRecord(value))
            throw new AccessRequestValidationError("La solicitud de acceso no es válida.");
        if (value.consentAccepted !== true) {
            throw new AccessRequestValidationError("Debés autorizar el tratamiento de datos y solicitar el acceso a PuntoPY.");
        }
        return {
            ok: true,
            value: {
                companyName: accessRequestText(value.companyName, "nombre de la empresa", 2, 120),
                fullName: accessRequestText(value.fullName, "nombre particular", 2, 120),
                email: accessRequestEmail(value.email),
                ruc: accessRequestRuc(value.ruc),
                website: accessRequestWebsite(value.website),
                consentAccepted: true,
                consentVersion: ACCESS_REQUEST_CONSENT_VERSION
            }
        };
    }
    catch (error) {
        return {
            ok: false,
            error: error instanceof AccessRequestValidationError ? error.message : "La solicitud de acceso no es válida."
        };
    }
}
class MerchantValidationError extends Error {
}
function merchantRecord(value, message) {
    if (typeof value !== "object" || value === null)
        throw new MerchantValidationError(message);
    return value;
}
function merchantText(value, label, minimum, maximum) {
    if (typeof value !== "string")
        throw new MerchantValidationError(`El campo ${label} es obligatorio.`);
    const normalized = value.trim().replace(/\s+/g, " ");
    if (normalized.length < minimum || normalized.length > maximum) {
        throw new MerchantValidationError(`${label} debe tener entre ${minimum} y ${maximum} caracteres.`);
    }
    return normalized;
}
function merchantOptionalText(value, label, maximum) {
    if (value === null || value === undefined || value === "")
        return "";
    if (typeof value !== "string")
        throw new MerchantValidationError(`El campo ${label} no es válido.`);
    const normalized = value.trim().replace(/\s+/g, " ");
    if (normalized.length > maximum)
        throw new MerchantValidationError(`${label} no puede superar ${maximum} caracteres.`);
    return normalized;
}
function merchantEmail(value) {
    if (value === null || value === undefined || value === "")
        return null;
    const email = merchantText(value, "correo de contacto", 5, 254).toLocaleLowerCase("en-US");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
        throw new MerchantValidationError("Ingresá un correo de contacto válido.");
    return email;
}
function merchantUrl(value, label = "sitio web") {
    if (value === null || value === undefined || value === "")
        return null;
    if (typeof value !== "string")
        throw new MerchantValidationError(`El ${label} no es válido.`);
    const trimmed = value.trim();
    if (trimmed.length > 240)
        throw new MerchantValidationError(`El ${label} no puede superar 240 caracteres.`);
    const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    try {
        const url = new URL(candidate);
        if ((url.protocol !== "https:" && url.protocol !== "http:") || url.hostname.length < 3 || url.username || url.password) {
            throw new Error("unsupported URL");
        }
        url.hash = "";
        return url.toString();
    }
    catch {
        throw new MerchantValidationError(`Ingresá un ${label} válido.`);
    }
}
export function normalizeParaguayPhone(value) {
    if (value === undefined || value === null || value === "")
        return null;
    if (typeof value !== "string")
        throw new MerchantValidationError("El teléfono no es válido.");
    let digits = value.replace(/[\s().-]/g, "");
    if (digits.startsWith("00"))
        digits = `+${digits.slice(2)}`;
    if (digits.startsWith("0"))
        digits = `+595${digits.slice(1)}`;
    if (digits.startsWith("595"))
        digits = `+${digits}`;
    if (!/^\+595[1-9]\d{7,8}$/.test(digits))
        throw new MerchantValidationError("Ingresá un teléfono de Paraguay válido, por ejemplo +595 981 123456.");
    return digits;
}
export const PUNTOPY_CONTENT_TERMS_VERSION = "puntopy-content-terms-v1";
export const MODERATION_TARGET_TYPES = ["BUSINESS", "PRODUCT"];
export const MODERATION_REPORT_REASONS = [
    "MISLEADING",
    "PROHIBITED",
    "INAPPROPRIATE",
    "INFRINGEMENT",
    "PERSONAL_DATA",
    "OTHER"
];
export const MODERATION_REPORT_STATUSES = ["OPEN", "DISMISSED", "CONTENT_REMOVED", "BUSINESS_SUSPENDED"];
export const MODERATION_REVIEW_ACTIONS = ["DISMISS", "REMOVE_CONTENT", "SUSPEND_BUSINESS"];
export function validateAccountInput(value) {
    try {
        const input = merchantRecord(value, "Los datos de la cuenta no son válidos.");
        if (input.accountType !== "BUYER" && input.accountType !== "BUSINESS")
            throw new MerchantValidationError("Elegí una cuenta de comprador o empresa.");
        if (input.consentAccepted !== true)
            throw new MerchantValidationError("Aceptá el tratamiento de tus datos según la política de privacidad.");
        const business = input.accountType === "BUSINESS";
        if (business && input.contentTermsAccepted !== true)
            throw new MerchantValidationError("Aceptá las condiciones de uso y publicación para administrar contenido de una empresa.");
        return { ok: true, value: {
                accountType: input.accountType,
                fullName: merchantText(input.fullName, "nombre", 2, 120),
                companyName: business ? merchantText(input.companyName, "nombre de la empresa", 2, 120) : null,
                ruc: business && input.ruc !== null && input.ruc !== undefined && input.ruc !== "" ? accessRequestRuc(input.ruc) : null,
                website: business ? merchantUrl(input.website) : null,
                ...(input.city !== undefined || input.cityCode !== undefined || input.district !== undefined ? merchantLocation(input, false) : {}),
                consentAccepted: true,
                ...(business ? { contentTermsAccepted: true } : {})
            } };
    }
    catch (error) {
        return { ok: false, error: error instanceof MerchantValidationError || error instanceof AccessRequestValidationError ? error.message : "Los datos de la cuenta no son válidos." };
    }
}
export function validateContentTermsAcceptance(value) {
    try {
        const input = merchantRecord(value, "La aceptación de condiciones no es válida.");
        if (input.accepted !== true || input.version !== PUNTOPY_CONTENT_TERMS_VERSION) {
            throw new MerchantValidationError("Leé y aceptá la versión vigente de las condiciones de uso y publicación.");
        }
        return { ok: true, value: { accepted: true, version: PUNTOPY_CONTENT_TERMS_VERSION } };
    }
    catch (error) {
        return { ok: false, error: error instanceof MerchantValidationError ? error.message : "La aceptación de condiciones no es válida." };
    }
}
function moderationIdentifier(value, label) {
    if (typeof value !== "string" || value.length < 8 || value.length > 128 || !/^[A-Za-z0-9._~-]+$/.test(value)) {
        throw new MerchantValidationError(`${label} no es válido.`);
    }
    return value;
}
export function validateModerationReport(value) {
    try {
        const input = merchantRecord(value, "El reporte no es válido.");
        if (!MODERATION_TARGET_TYPES.includes(input.targetType))
            throw new MerchantValidationError("Elegí un contenido válido para reportar.");
        if (!MODERATION_REPORT_REASONS.includes(input.reason))
            throw new MerchantValidationError("Elegí un motivo válido para el reporte.");
        const targetType = input.targetType;
        const productId = targetType === "PRODUCT" ? moderationIdentifier(input.productId, "El producto") : null;
        const details = merchantOptionalText(input.details, "detalle del reporte", 1000) || null;
        if (input.reason === "OTHER" && (details?.length ?? 0) < 8)
            throw new MerchantValidationError("Explicá el motivo del reporte con al menos 8 caracteres.");
        return { ok: true, value: {
                targetType,
                businessId: moderationIdentifier(input.businessId, "El comercio"),
                productId,
                reason: input.reason,
                details
            } };
    }
    catch (error) {
        return { ok: false, error: error instanceof MerchantValidationError ? error.message : "El reporte no es válido." };
    }
}
export function validateModerationReview(value) {
    try {
        const input = merchantRecord(value, "La decisión de moderación no es válida.");
        if (!MODERATION_REVIEW_ACTIONS.includes(input.action))
            throw new MerchantValidationError("Elegí una decisión de moderación válida.");
        return { ok: true, value: {
                action: input.action,
                note: merchantText(input.note, "observación de moderación", 8, 500)
            } };
    }
    catch (error) {
        return { ok: false, error: error instanceof MerchantValidationError ? error.message : "La decisión de moderación no es válida." };
    }
}
function merchantLocation(input, required) {
    const cityName = required ? merchantText(input.city, "ciudad", 2, 80) : merchantOptionalText(input.city, "ciudad", 80);
    const district = merchantOptionalText(input.district, "barrio o localidad", 100);
    if (district && !cityName)
        throw new MerchantValidationError("Elegí primero la ciudad del barrio o localidad.");
    const explicitCode = input.cityCode !== undefined && input.cityCode !== null && input.cityCode !== "";
    if (explicitCode && (typeof input.cityCode !== "string" || !/^\d{4}$/.test(input.cityCode)))
        throw new MerchantValidationError("El código de ciudad no es válido.");
    const city = findParaguayCity(cityName, explicitCode ? input.cityCode : null);
    if (explicitCode && (!city || normalizeCatalogText(city.name) !== normalizeCatalogText(cityName)))
        throw new MerchantValidationError("La ciudad no coincide con su departamento. Volvé a seleccionarla.");
    return { city: explicitCode && city ? city.name : cityName, cityCode: city?.code ?? null, district };
}
export function validateMerchantInviteInput(value) {
    try {
        const input = merchantRecord(value, "La invitación no es válida.");
        const expiresInDays = input.expiresInDays === undefined ? 30 : input.expiresInDays;
        if (typeof expiresInDays !== "number" || !Number.isInteger(expiresInDays) || expiresInDays < 1 || expiresInDays > 90) {
            throw new MerchantValidationError("La invitación debe vencer entre 1 y 90 días.");
        }
        return {
            ok: true,
            value: {
                businessName: merchantText(input.businessName, "nombre del comercio", 2, 120),
                ...merchantLocation(input, true),
                category: merchantText(input.category, "rubro", 2, 80),
                contactEmail: merchantEmail(input.contactEmail),
                expiresInDays
            }
        };
    }
    catch (error) {
        return { ok: false, error: error instanceof MerchantValidationError ? error.message : "La invitación no es válida." };
    }
}
function merchantCoordinates(latValue, lngValue) {
    const latEmpty = latValue === null || latValue === undefined || latValue === "";
    const lngEmpty = lngValue === null || lngValue === undefined || lngValue === "";
    if (latEmpty && lngEmpty)
        return { lat: null, lng: null };
    const lat = typeof latValue === "number" ? latValue : Number(latValue);
    const lng = typeof lngValue === "number" ? lngValue : Number(lngValue);
    if (!Number.isFinite(lat) || !Number.isFinite(lng))
        throw new MerchantValidationError("La latitud y longitud deben ser números válidos.");
    if (lat < -27.7 || lat > -19.2 || lng < -62.7 || lng > -54.1) {
        throw new MerchantValidationError("La ubicación debe encontrarse dentro de Paraguay.");
    }
    return { lat, lng };
}
export function validateMerchantBusinessProfile(value) {
    try {
        const input = merchantRecord(value, "La ficha del comercio no es válida.");
        if (typeof input.channelType !== "string" || !PUBLIC_CHANNEL_TYPES.includes(input.channelType)) {
            throw new MerchantValidationError("Seleccioná un tipo de atención válido.");
        }
        const channelType = input.channelType;
        const websiteUrl = merchantUrl(input.websiteUrl);
        const contactPhone = normalizeParaguayPhone(input.contactPhone);
        const addressLabel = merchantOptionalText(input.addressLabel, "dirección", 180);
        const coordinates = merchantCoordinates(input.lat, input.lng);
        const publish = input.publish === true;
        const publicationConsent = input.publicationConsent === true;
        if (publish && !publicationConsent)
            throw new MerchantValidationError("Debés autorizar la publicación de los datos del comercio.");
        if (publish && channelType === "UNKNOWN")
            throw new MerchantValidationError("Seleccioná si el comercio atiende de forma física, online o en ambas modalidades.");
        if (publish && (channelType === "PHYSICAL_LOCATION" || channelType === "ONLINE_AND_PHYSICAL") && (addressLabel.length < 3 || coordinates.lat === null)) {
            throw new MerchantValidationError("Para publicar una ubicación física, completá la dirección y sus coordenadas.");
        }
        if (publish && websiteUrl === null && contactPhone === null) {
            throw new MerchantValidationError("Agregá un sitio web o un teléfono para que los compradores puedan contactar al comercio.");
        }
        return {
            ok: true,
            value: {
                name: merchantText(input.name, "nombre del comercio", 2, 120),
                category: merchantText(input.category, "rubro", 2, 80),
                ...merchantLocation(input, true),
                addressLabel,
                channelType,
                websiteUrl,
                contactPhone,
                whatsappEnabled: input.whatsappEnabled === true && contactPhone !== null,
                description: merchantOptionalText(input.description, "descripción del comercio", 500) || null,
                ...coordinates,
                publish,
                publicationConsent
            }
        };
    }
    catch (error) {
        return { ok: false, error: error instanceof MerchantValidationError ? error.message : "La ficha del comercio no es válida." };
    }
}
export function validateMerchantProduct(value) {
    try {
        const input = merchantRecord(value, "El producto no es válido.");
        if (typeof input.availability !== "string" || !AVAILABILITY_STATUSES.includes(input.availability)) {
            throw new MerchantValidationError("Seleccioná una disponibilidad válida.");
        }
        let pricePyg = null;
        if (input.pricePyg !== null && input.pricePyg !== undefined && input.pricePyg !== "") {
            const candidate = typeof input.pricePyg === "number" ? input.pricePyg : Number(input.pricePyg);
            if (!Number.isSafeInteger(candidate) || candidate < 0 || candidate > 999_999_999_999) {
                throw new MerchantValidationError("El precio debe ser un monto entero válido en guaraníes.");
            }
            pricePyg = candidate;
        }
        const publish = input.publish === true;
        const publicationConsent = input.publicationConsent === true;
        if (publish && !publicationConsent)
            throw new MerchantValidationError("Debés confirmar que el producto y sus datos pertenecen al comercio.");
        const initialStock = input.initialStock === null || input.initialStock === undefined || input.initialStock === "" ? null : merchantStockNumber(input.initialStock);
        const lowStockThreshold = input.lowStockThreshold === undefined || input.lowStockThreshold === "" ? 5 : merchantStockNumber(input.lowStockThreshold);
        if (lowStockThreshold > 9999)
            throw new MerchantValidationError("El umbral de pocas unidades no puede superar 9999.");
        const mediaIds = input.mediaIds === undefined ? undefined : input.mediaIds;
        if (mediaIds !== undefined && (!Array.isArray(mediaIds) || mediaIds.length > 6 || mediaIds.some((id) => typeof id !== "string" || !/^[a-zA-Z0-9-]{16,80}$/.test(id)) || new Set(mediaIds).size !== mediaIds.length))
            throw new MerchantValidationError("Seleccioná hasta 6 archivos válidos sin repetir.");
        return {
            ok: true,
            value: {
                name: merchantText(input.name, "nombre del producto", 2, 120),
                category: merchantText(input.category, "categoría", 2, 80),
                description: merchantOptionalText(input.description, "descripción", 280) || null,
                pricePyg,
                availability: input.availability,
                websiteUrl: merchantUrl(input.websiteUrl, "enlace del producto"),
                imageUrl: merchantImageUrl(input.imageUrl),
                publish,
                publicationConsent,
                ...(input.sku === undefined ? {} : { sku: merchantOptionalText(input.sku, "código interno", 80) || null }),
                initialStock,
                ...(input.lowStockThreshold === undefined ? {} : { lowStockThreshold }),
                ...(mediaIds === undefined ? {} : { mediaIds: mediaIds })
            }
        };
    }
    catch (error) {
        return { ok: false, error: error instanceof MerchantValidationError ? error.message : "El producto no es válido." };
    }
}
function merchantStockNumber(value) {
    if ((typeof value !== "number" && typeof value !== "string") || String(value).trim() === "")
        throw new MerchantValidationError("Ingresá una cantidad entera válida.");
    const number = Number(value);
    if (!Number.isSafeInteger(number) || number < 0 || number > 1_000_000)
        throw new MerchantValidationError("La cantidad debe ser un entero entre 0 y 1.000.000.");
    return number;
}
export function availabilityForStock(quantity, threshold, manual) {
    if (quantity === null)
        return manual;
    if (quantity === 0)
        return "OUT_OF_STOCK";
    return quantity <= threshold ? "LOW_STOCK" : "IN_STOCK";
}
export function validateStockAdjustment(value) {
    try {
        const input = merchantRecord(value, "El movimiento no es válido.");
        if (typeof input.requestId !== "string" || !/^[a-zA-Z0-9-]{16,80}$/.test(input.requestId))
            throw new MerchantValidationError("El identificador del movimiento no es válido.");
        if (!["SET", "IN", "OUT", "MANUAL"].includes(String(input.operation)))
            throw new MerchantValidationError("Seleccioná el tipo de movimiento.");
        const operation = input.operation;
        const quantity = operation === "MANUAL" ? null : merchantStockNumber(input.quantity);
        if ((operation === "IN" || operation === "OUT") && quantity === 0)
            throw new MerchantValidationError("Las entradas y salidas requieren al menos una unidad.");
        const availability = input.availability ?? "UNKNOWN";
        if (!AVAILABILITY_STATUSES.includes(availability))
            throw new MerchantValidationError("Seleccioná una disponibilidad válida.");
        const expectedVersion = merchantStockNumber(input.expectedVersion);
        return { ok: true, value: { requestId: input.requestId, operation, quantity, availability: availability, expectedVersion, reason: merchantText(input.reason, "motivo", 3, 160) } };
    }
    catch (error) {
        return { ok: false, error: error instanceof MerchantValidationError ? error.message : "El movimiento no es válido." };
    }
}
export function applyStockAdjustment(product, input) {
    const version = product.stockVersion ?? 0;
    if (version !== input.expectedVersion)
        return { ok: false, error: "El inventario cambió en otra sesión. Revisá las existencias actualizadas antes de volver a aplicar." };
    const before = product.stockQuantity ?? null;
    if ((input.operation === "IN" || input.operation === "OUT") && before === null)
        return { ok: false, error: "Primero indicá las existencias iniciales con ‘Ajustar a’." };
    const after = input.operation === "MANUAL" ? null : input.operation === "SET" ? input.quantity : (before ?? 0) + (input.operation === "IN" ? 1 : -1) * (input.quantity ?? 0);
    if (after !== null && (after < 0 || after > 1_000_000))
        return { ok: false, error: after < 0 ? "No hay existencias suficientes para registrar esa salida." : "El inventario no puede superar 1.000.000 de unidades." };
    return { ok: true, value: { stockQuantity: after, stockVersion: version + 1, availability: availabilityForStock(after, product.lowStockThreshold ?? 5, input.availability) } };
}
function merchantImageUrl(value) {
    const url = merchantUrl(value, "enlace de imagen");
    if (url !== null && !url.startsWith("https://"))
        throw new MerchantValidationError("La imagen debe usar una dirección HTTPS pública.");
    return url;
}
